import { JsToClientInterface } from './jsToClientInterface';
import { JsWebglInterface } from './webgl/jsWebglInterface';
import { ClientToJsInterface } from './clientToJsInterface';
import { WebglJsInterface } from './webgl/webglJsInterface';
import consoleLogger from '../logger/consoleLogger';
import { JsToClientCommand } from './jsToClientCommand';
import { WebglClient } from './webgl/webglClient';
import { AClient } from './aClient';
import { DownloadScreen } from './webgl/downloadScreen';
import { ClientType } from './clientType';
import { EmscriptenClient } from './emscripten/emscriptenClient';
import { EmscriptenJsInterface } from './emscripten/emscriptenJsInterface';
import { EmscriptenClientJsInterface } from './emscripten/emscriptenClientJsInterface';

const logger = consoleLogger.getPrefixedCopy('GAME_CLIENT');
let clientApiReady = false;

export enum GameClientLoadingState {
    FAILED,
    ABSENT,
    EMBEDDED,
    LOADING,
    LOADED
}

export class GameClient extends AClient {
    private static instance: GameClient;
    private clientToJsInterface: ClientToJsInterface;
    private jsToClientInterface: JsToClientInterface;
    private loadingState: GameClientLoadingState = GameClientLoadingState.ABSENT;

    private constructor() {
        super();
        this.clientToJsInterface = GameClient.getClientToJsInterface();
        this.clientToJsInterface.expose();
        this.jsToClientInterface = GameClient.getJsToClientInterface();
        logger.info('Constructor complete');
    }

    public static getInstance(): GameClient {
        if (typeof GameClient.instance === 'undefined') {
            GameClient.instance = new GameClient();
        }

        return GameClient.instance;
    }

    public static clientApiReady(): Promise<void> {
        if (clientApiReady === true) {
            return new Promise((resolve) => resolve());
        }

        const promise = GameClient.getCurrentClient().clientApiReady();

        promise.then(() => {
            logger.info('saving api ready state');
            clientApiReady = true;
        });

        return promise;
    }

    public static getCurrentClient(): typeof AClient {
        return window.app.clientType === ClientType.EMSCRIPTEN ? EmscriptenClient : WebglClient;
    }


    public static embed(): void {
        if (!window.app.isDesktop) {
            DownloadScreen.show();
            return;
        }

        this.getCurrentClient().embed();
    }

    public static show(): void {
        this.getCurrentClient().show();
    }

    public static hide(): void {
        this.getCurrentClient().hide();
    }

    public static setClientSize(_widthPercent: number, _heightPercent: number): void {
        this.getCurrentClient().setClientSize(_widthPercent, _heightPercent);
    }

    public static stretchClient(): void {
        this.getCurrentClient().stretchClient();
    }

    public static getClientToJsInterface(): ClientToJsInterface {
        return window.app.clientType === ClientType.EMSCRIPTEN
            ? new EmscriptenClientJsInterface()
            : new WebglJsInterface();
    }

    public static getJsToClientInterface(): JsToClientInterface {
        return window.app.clientType === ClientType.EMSCRIPTEN
            ? new EmscriptenJsInterface()
            : new JsWebglInterface();
    }

    public setLoadingState(newState: GameClientLoadingState): void {
        this.loadingState = newState;
    }

    public getLoadingState(): GameClientLoadingState {
        return this.loadingState;
    }


    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async sendCommand<T>(command: JsToClientCommand<any>): Promise<T> {
        let result: T;
        logger.info('sending client command', command);
        result = this.jsToClientInterface.sendCommand<T>(command);
        logger.info(`Command ${command.getName()} result`, result);
        return result;
    }

    public openAccountWindow(): void {
        this.jsToClientInterface.openAccountWindow();
    }
}
